import { useState, useRef, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MessageCircle, X, Send, Bot, LifeBuoy } from "lucide-react";
import { WellyQuickReplies, wellyGreeting } from "@/components/WellyQuickReplies";

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() =>
    (window.crypto?.randomUUID && window.crypto.randomUUID()) || String(Date.now()));
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: wellyGreeting() }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (e, preset) => {
    e?.preventDefault();
    const text = (preset ?? input).trim();
    if (!text || sending) return;
    if (preset === undefined) setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, {
        role: "assistant", content: data.answer,
        caseId: data.case_id, needsHuman: data.needs_human,
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.",
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="support-widget-toggle"
        aria-label="Support"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="support-widget-panel"
          className="fixed bottom-24 right-3 sm:right-5 z-50 flex h-[min(520px,calc(100dvh-8rem))] w-[min(400px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
            <LifeBuoy className="h-5 w-5" />
            <div>
              <p className="text-sm font-bold leading-tight">Ask Welly</p>
              <p className="text-[11px] text-slate-300">Eligvia's help assistant · from your account + help centre</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="support-messages">
            {messages.map((m, i) => (
              <div key={i} data-testid={`support-msg-${m.role === "user" ? "user" : "bot"}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === "user"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-800"
                  }`}
                >
                  {m.role === "assistant" && (
                    <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                      <Bot className="h-3.5 w-3.5" /> Welly
                    </div>
                  )}
                  {m.content}
                  {m.needsHuman && m.caseId && (
                    <div className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700" data-testid="support-case-note">
                      A support case was created — a human will follow up if needed. Ref: {m.caseId.slice(0, 8)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {messages.length === 1 && <WellyQuickReplies disabled={sending} onPick={(q) => send(null, q.message)} />}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-slate-100 px-3.5 py-2.5 text-sm text-slate-400">Thinking…</div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-200 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              data-testid="support-input"
              className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-slate-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="support-send-btn"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
